import { useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Card, CardContent, CardHeader, CardTitle, Skeleton } from "@adora/shadcn-ui";
import { Wallet } from "lucide-react";
import PageShadcn from "@adora/shared/components/layout/page-shadcn";
import useBrandName from "../hooks/use-brand-name";
import { useTokenWallet } from "../hooks/use-token-wallet";
import CreateWalletDialog from "../components/token-admin/create-wallet-dialog";
import AdjustBalanceDialog from "../components/token-admin/adjust-balance-dialog";
import AdjustWalletDialog from "../components/token-admin/adjust-wallet-dialog";

type ActiveDialog = "create" | "topup" | "deduction" | "allotment" | "cap" | null;

const Stat = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col gap-1">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="text-2xl font-medium">{value}</span>
  </div>
);

export default function TokenManagement() {
  const { brandId: brandIdParam } = useParams();
  const parsedBrandId = Number(brandIdParam);
  const isValidBrandId = Number.isInteger(parsedBrandId) && parsedBrandId > 0;
  const brandId = isValidBrandId ? parsedBrandId : null;
  const brandName = useBrandName(brandIdParam);

  const { data: wallet, isLoading, isError, error, refetch } = useTokenWallet(brandId);
  const [activeDialog, setActiveDialog] = useState<ActiveDialog>(null);

  const close = () => setActiveDialog(null);

  return (
    <PageShadcn
      title="Manage Tokens"
      description={brandName ? `Generation-token wallet for ${brandName}` : "Generation-token wallet"}
      backLinkText="Brands"
      backLinkPath="/brands"
      testId="token-management-page"
    >
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-6 py-8 text-left">
        {!isValidBrandId && (
          <Card>
            <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
              <p className="font-medium">Invalid brand</p>
              <p className="text-sm text-muted-foreground">&quot;{brandIdParam}&quot; is not a valid brand id.</p>
            </CardContent>
          </Card>
        )}

        {isValidBrandId && isLoading && <Skeleton className="h-48 w-full rounded-lg" />}

        {isValidBrandId && !isLoading && isError && (
          <Card>
            <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
              <p className="text-sm text-destructive">{error?.message || "Failed to load wallet."}</p>
              <Button variant="outline" onClick={() => void refetch()}>
                Retry
              </Button>
            </CardContent>
          </Card>
        )}

        {isValidBrandId && !isLoading && !isError && !wallet && (
          <Card>
            <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
              <Wallet className="h-10 w-10 text-muted-foreground" />
              <div className="flex flex-col gap-1">
                <p className="font-medium">No wallet yet</p>
                <p className="text-sm text-muted-foreground">This brand has no generation-token wallet.</p>
              </div>
              <Button onClick={() => setActiveDialog("create")}>Create Wallet</Button>
            </CardContent>
          </Card>
        )}

        {isValidBrandId && !isLoading && !isError && wallet && (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="capitalize">{wallet.planName} plan</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-6">
                <Stat label="Balance" value={wallet.balance.toLocaleString()} />
                <Stat label="Monthly allotment" value={wallet.monthlyAllotment.toLocaleString()} />
                <Stat label="Rollover cap" value={wallet.rolloverCap.toLocaleString()} />
                <Stat label="Wallet type" value={wallet.walletType} />
              </CardContent>
            </Card>

            <div className="flex flex-wrap gap-3">
              <Button onClick={() => setActiveDialog("topup")}>Top Up Balance</Button>
              <Button variant="outline" onClick={() => setActiveDialog("deduction")}>
                Deduct Balance
              </Button>
              <Button variant="outline" onClick={() => setActiveDialog("allotment")}>
                Adjust Allotment
              </Button>
              <Button variant="outline" onClick={() => setActiveDialog("cap")}>
                Adjust Rollover Cap
              </Button>
            </div>
          </>
        )}
      </div>

      {brandId != null && (
        <>
          <CreateWalletDialog open={activeDialog === "create"} onOpenChange={close} brandId={brandId} />
          <AdjustBalanceDialog
            open={activeDialog === "topup"}
            onOpenChange={close}
            brandId={brandId}
            variant="balance_topup"
          />
          <AdjustBalanceDialog
            open={activeDialog === "deduction"}
            onOpenChange={close}
            brandId={brandId}
            variant="balance_deduction"
          />
          <AdjustWalletDialog
            open={activeDialog === "allotment"}
            onOpenChange={close}
            brandId={brandId}
            field="set_monthly_allotment"
            currentValue={wallet?.monthlyAllotment ?? 0}
            counterpartValue={wallet?.rolloverCap ?? 0}
          />
          <AdjustWalletDialog
            open={activeDialog === "cap"}
            onOpenChange={close}
            brandId={brandId}
            field="set_rollover_cap"
            currentValue={wallet?.rolloverCap ?? 0}
            counterpartValue={wallet?.monthlyAllotment ?? 0}
          />
        </>
      )}
    </PageShadcn>
  );
}
